import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CIRCLE_MEMBER_ROLES,
  CIRCLE_STATUS,
  type CircleMemberRole,
  type CircleStatus,
} from "@connect/shared";
import { useApi } from "@/lib/api";
import { AdminNav } from "@/components/admin/AdminNav";

interface AdminCircle {
  id: string;
  slug: string;
  name: string;
  kind: string;
  is_private: number;
  status: CircleStatus;
  member_count: number;
  pending_count: number;
  created_at: number;
}

interface Member {
  id: string;
  display_name: string | null;
  email: string;
  role: CircleMemberRole;
  status: "active" | "pending";
  joined_at: number;
}

export function AdminCircles() {
  const api = useApi();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<CircleStatus | "all">("all");
  const [openCircle, setOpenCircle] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin-circles", statusFilter],
    queryFn: () =>
      api.get<{ circles: AdminCircle[] }>(
        `/admin/circles${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`,
      ),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CircleStatus }) =>
      api.patch(`/admin/circles/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-circles"] }),
  });

  const circles = list.data?.circles ?? [];

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-8">
        <p className="label-eyebrow">Operations</p>
        <h1 className="mt-2 text-display-2 font-display">Circles</h1>
        <p className="mt-3 text-whisper text-sm max-w-xl leading-relaxed">
          Archive, unarchive, and manage circle membership. Pending join
          requests show here for approval.
        </p>
      </header>

      <AdminNav />

      <div className="mb-6 flex items-center gap-5 text-[10px] uppercase tracking-[0.18em]">
        {(["all", ...CIRCLE_STATUS] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={`pb-2 border-b transition-colors ${
              statusFilter === f
                ? "text-fog border-gold-glow"
                : "text-whisper border-transparent hover:text-fog"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {list.isLoading ? (
        <p className="text-sm text-whisper">Reading circles…</p>
      ) : circles.length === 0 ? (
        <p className="text-sm text-whisper">No circles in this view.</p>
      ) : (
        <div className="panel divide-y divide-veil/40">
          {circles.map((c) => (
            <div key={c.id}>
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-6 px-6 py-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
                    {c.kind} · {c.status}
                    {c.is_private === 1 && <span> · private</span>}
                  </div>
                  <div className="mt-1 font-display text-sm">{c.name}</div>
                  <div className="mt-1 text-xs text-whisper">
                    {c.member_count} members
                    {c.pending_count > 0 && (
                      <span className="text-gold-glow"> · {c.pending_count} pending</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-whisper">
                  {c.status !== "active" && (
                    <button
                      type="button"
                      onClick={() => setStatus.mutate({ id: c.id, status: "active" })}
                      className="hover:text-fog disabled:opacity-40"
                      disabled={setStatus.isPending}
                    >
                      Activate
                    </button>
                  )}
                  {c.status !== "archived" && (
                    <button
                      type="button"
                      onClick={() => setStatus.mutate({ id: c.id, status: "archived" })}
                      className="hover:text-fog disabled:opacity-40"
                      disabled={setStatus.isPending}
                    >
                      Archive
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpenCircle(openCircle === c.id ? null : c.id)}
                  className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog"
                >
                  {openCircle === c.id ? "Hide members" : "Members"}
                </button>
              </div>
              {openCircle === c.id && <MembersPanel circleId={c.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MembersPanel({ circleId }: { circleId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin-circle-members", circleId],
    queryFn: () => api.get<{ members: Member[] }>(`/admin/circles/${circleId}/members`),
  });
  const patch = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/circle-members/${circleId}/${userId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-circle-members", circleId] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) =>
      api.del(`/admin/circle-members/${circleId}/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-circle-members", circleId] });
      qc.invalidateQueries({ queryKey: ["admin-circles"] });
    },
  });

  if (list.isLoading) {
    return <div className="px-6 py-4 text-xs text-whisper border-t border-veil/40">Loading…</div>;
  }
  const members = list.data?.members ?? [];
  return (
    <div className="bg-ink-elev/30 border-t border-veil/40 divide-y divide-veil/30">
      {members.length === 0 ? (
        <div className="px-6 py-4 text-xs text-whisper">No members.</div>
      ) : (
        members.map((m) => (
          <div
            key={m.id}
            className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-6 py-3 text-xs"
          >
            <div className="min-w-0">
              <div className="text-fog truncate">{m.display_name ?? "—"}</div>
              <div className="text-whisper truncate">{m.email}</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
              {m.status === "pending" ? "pending" : m.role}
            </div>
            {m.status === "pending" ? (
              <button
                type="button"
                onClick={() => patch.mutate({ userId: m.id, body: { status: "active" } })}
                disabled={patch.isPending}
                className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog disabled:opacity-40"
              >
                Approve
              </button>
            ) : (
              <select
                value={m.role}
                onChange={(e) => patch.mutate({ userId: m.id, body: { role: e.target.value } })}
                disabled={patch.isPending}
                className="bg-ink-elev/60 border border-veil/50 rounded px-2 py-1 text-xs text-fog focus:outline-none"
              >
                {CIRCLE_MEMBER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => remove.mutate(m.id)}
              disabled={remove.isPending}
              className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  );
}
